import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { Loader2 } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import AssetPhotoGrid from "@/features/assets/components/AssetPhotoGrid";
import type { Asset, EditAssetFormData } from "./editAssetModal/types";
import { useAssetCategories } from "./editAssetModal/useAssetCategories";
import { BasicInfoFields } from "./editAssetModal/BasicInfoFields";
import { DateAndSpecFields } from "./editAssetModal/DateAndSpecFields";
import { submitAssetUpdate } from "./editAssetModal/submitAssetUpdate";

interface EditAssetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: Asset;
  onAssetUpdated: () => void;
}

const INITIAL_FORM_DATA: EditAssetFormData = {
  name: "",
  manufacturer: "",
  model_number: "",
  serial_number: "",
  status: "Active",
  purchase_date: undefined,
  purchase_price: "",
  warranty_expires: undefined,
  last_maintenance: undefined,
  next_maintenance: undefined,
  notes: "",
  asset_code: "",
  condition: "Good",
  installation_date: undefined,
};

export function EditAssetModal({ open, onOpenChange, asset, onAssetUpdated }: EditAssetModalProps) {
  const { toast } = useToast();
  const categories = useAssetCategories();
  const [loading, setLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [dynamicAttributes, setDynamicAttributes] = useState<Record<string, any>>({});
  const [formData, setFormData] = useState<EditAssetFormData>(INITIAL_FORM_DATA);

  // Initialize form with asset data
  useEffect(() => {
    if (asset && open) {
      setFormData({
        name: asset.name || "",
        manufacturer: asset.manufacturer || "",
        model_number: asset.model_number || "",
        serial_number: asset.serial_number || "",
        status: asset.status || "Active",
        purchase_date: asset.purchase_date ? new Date(asset.purchase_date) : undefined,
        purchase_price: asset.purchase_price?.toString() || "",
        warranty_expires: asset.warranty_expires ? new Date(asset.warranty_expires) : undefined,
        last_maintenance: asset.last_maintenance ? new Date(asset.last_maintenance) : undefined,
        next_maintenance: asset.next_maintenance ? new Date(asset.next_maintenance) : undefined,
        notes: asset.notes || "",
        asset_code: asset.asset_code || "",
        condition: (asset.condition as any) || "Good",
        installation_date: asset.installation_date ? new Date(asset.installation_date) : undefined,
      });

      setSelectedCategoryId(asset.category_id || '');
      setDynamicAttributes(asset.category_attributes || {});
    }
  }, [asset, open]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    const cat = categories.find(c => c.id === categoryId);
    const initialAttrs: Record<string, any> = {};
    if (cat?.schema?.properties) {
      Object.entries(cat.schema.properties).forEach(([key, prop]: [string, any]) => {
        if (categoryId === asset.category_id && asset.category_attributes?.[key] !== undefined) {
          initialAttrs[key] = asset.category_attributes[key];
        } else {
          initialAttrs[key] = prop.default !== undefined ? prop.default : '';
        }
      });
    }
    setDynamicAttributes(initialAttrs);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({ title: "Error", description: "Asset name is required.", variant: "destructive" });
      return;
    }

    if (!selectedCategoryId) {
      toast({ title: "Error", description: "Category is required.", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      await submitAssetUpdate({
        assetId: asset.id,
        formData,
        selectedCategoryId,
        dynamicAttributes,
      });

      toast({ title: "Success", description: "Asset updated successfully!" });

      onAssetUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating asset:', error);
      toast({
        title: "Error",
        description: "Failed to update asset. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Asset</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <BasicInfoFields
            formData={formData}
            onFormChange={setFormData}
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            onCategoryChange={handleCategoryChange}
          />

          <DateAndSpecFields
            formData={formData}
            onFormChange={setFormData}
            selectedCategory={selectedCategory}
            dynamicAttributes={dynamicAttributes}
            onDynamicAttributesChange={setDynamicAttributes}
          />

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes..."
              rows={3}
            />
          </div>

          {/* Photos Section */}
          <div className="space-y-2">
            <Label>Photos</Label>
            <AssetPhotoGrid assetId={asset.id} />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
